/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

// Service to manage native browser notifications with robust fallbacks
export class NotificationService {
  private static audioCtx: AudioContext | null = null;

  // Check if browser notifications are supported
  static isSupported(): boolean {
    return typeof window !== 'undefined' && 'Notification' in window;
  }

  // Get current permission status
  static getPermission(): 'granted' | 'denied' | 'default' {
    if (!this.isSupported()) return 'denied';
    return Notification.permission;
  }

  // Request browser permission for native notifications
  static async requestPermission(): Promise<'granted' | 'denied' | 'default'> {
    if (!this.isSupported()) return 'denied';
    
    try {
      // Check if we are inside a sandboxed iframe where permission requests are forbidden
      if (window.self !== window.top) {
        console.warn("Notification request skipped: running inside an iframe.");
        return 'default';
      }
      
      const permission = await Notification.requestPermission();
      return permission;
    } catch (error) {
      console.warn("Failed to request native notification permission (likely iframe sandbox restriction):", error);
      return 'default';
    }
  }

  // Play a beautiful, customized dual-tone synthesizer alert in the browser
  static playChime(type: 'info' | 'success' | 'warning' | 'critical' = 'info') {
    try {
      const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtxClass) return;

      if (!this.audioCtx) {
        this.audioCtx = new AudioCtxClass();
      }

      const ctx = this.audioCtx;
      if (ctx.state === 'suspended') {
        ctx.resume();
      }

      const now = ctx.currentTime;
      
      // Determine frequencies based on alert type
      let freq1 = 523.25; // C5
      let freq2 = 659.25; // E5
      let duration = 0.3;

      if (type === 'success') {
        freq1 = 523.25; // C5
        freq2 = 783.99; // G5
        duration = 0.4;
      } else if (type === 'warning') {
        freq1 = 440.00; // A4
        freq2 = 554.37; // C#5
        duration = 0.5;
      } else if (type === 'critical') {
        freq1 = 349.23; // F4
        freq2 = 392.00; // G4
        duration = 0.6;
      }

      // First tone osc
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(freq1, now);
      osc1.frequency.exponentialRampToValueAtTime(freq2, now + duration);

      // Gain envelope
      gain1.gain.setValueAtTime(0.15, now);
      gain1.gain.exponentialRampToValueAtTime(0.01, now + duration);

      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.start(now);
      osc1.stop(now + duration);

      // Slower second harmonic tone for full sound
      if (type === 'critical' || type === 'warning') {
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.type = 'triangle';
        osc2.frequency.setValueAtTime(freq1 / 2, now); // Octave below
        osc2.frequency.exponentialRampToValueAtTime(freq2 / 2, now + duration);
        
        gain2.gain.setValueAtTime(0.08, now);
        gain2.gain.exponentialRampToValueAtTime(0.01, now + duration);

        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.start(now);
        osc2.stop(now + duration);
      }

    } catch (e) {
      console.warn("Could not play notification synthesizer chime:", e);
    }
  }

  // Send a native notification (or log fallback if denied/iframe-sandboxed)
  static sendNotification(title: string, options: NotificationOptions = {}, type: 'info' | 'success' | 'warning' | 'critical' = 'info'): boolean {
    // Play the physical sound
    this.playChime(type);

    if (!this.isSupported()) {
      console.log(`[Notification Fallback] ${title}: ${options.body}`);
      return false;
    }

    if (Notification.permission === 'granted') {
      try {
        // Enforce basic options
        const notification = new Notification(title, {
          icon: 'https://www.grupoisn.com.mx/wp-content/uploads/2025/12/cropped-WhatsApp-Image-2025-12-19-at-12.31.53.jpeg',
          silent: false,
          ...options
        });

        // Auto close after 5 seconds
        setTimeout(() => notification.close(), 5000);
        return true;
      } catch (err) {
        console.warn("Failed to trigger native Notification object (possibly sandbox restriction):", err);
        return false;
      }
    } else {
      console.log(`[Notification Fallback (No Permission)] ${title}: ${options.body}`);
      return false;
    }
  }
}
